import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSocket } from './useSocket';

/**
 * Hook that connects to WebSocket and processes AI UI commands.
 * Must be mounted within a Router context.
 */
export function useAIController() {
  const navigate = useNavigate();
  const { sendMessage, executeTool, executeToolAsync, sendContinue, sendStop } = useSocket();

  const resolveNavigationPath = (detail: unknown): string | null => {
    if (!detail || typeof detail !== 'object') return null;
    const payload = detail as { path?: unknown; url?: unknown; fullUrl?: unknown };
    const raw = payload.path ?? payload.url ?? payload.fullUrl;
    if (typeof raw !== 'string' || !raw.trim()) return null;

    if (raw.startsWith('/')) return raw;
    try {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.origin !== window.location.origin) return null;
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const handleNavigate = (e: Event) => {
      const path = resolveNavigationPath((e as CustomEvent).detail);
      if (path) {
        navigate(path);
      }
    };

    const handleToast = (e: Event) => {
      const detail = (e as CustomEvent).detail as { message: string; type?: string };
      if (!detail?.message) return;
      switch (detail.type) {
        case 'success': toast.success(detail.message); break;
        case 'error': toast.error(detail.message); break;
        default: toast.info(detail.message);
      }
    };

    window.addEventListener('ai:navigate', handleNavigate);
    window.addEventListener('ai:toast', handleToast);

    return () => {
      window.removeEventListener('ai:navigate', handleNavigate);
      window.removeEventListener('ai:toast', handleToast);
    };
  }, [navigate]);

  return { sendMessage, executeTool, executeToolAsync, sendContinue, sendStop };
}
